import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Tabs, Tab, IconButton,
  CircularProgress, Popover
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const API_BASE = process.env.REACT_APP_API_URL;

const applications = [
  process.env.REACT_APP_VAIDHYAMEGHA_APP_NAME,
  process.env.REACT_APP_BIM_APP_NAME,
  process.env.REACT_APP_CSDLC_APP_NAME,
  process.env.REACT_APP_SAMYAMA_APP_NAME,
  process.env.REACT_APP_SERVICE_GATEWAY_APP_NAME
].filter(Boolean); // Filter out any undefined values
const tabs = ['HEALTH CHECKS', 'AUTOMATION TEST CASES'];
const environments = ['DEV', 'PROD'];

function QADashboard() {
  const [selectedApp, setSelectedApp] = useState(applications[0]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedEnv, setSelectedEnv] = useState('DEV');

  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]);
  const [automationRecords, setAutomationRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Track last fetch time for health records
  const [lastHealthFetchTime, setLastHealthFetchTime] = useState(null);


  const formatDate = (date) => date.toLocaleDateString('sv-SE');

  useEffect(() => {
    let intervalId;
  
    // 🔹 Handle Automation Tab Early
    if (selectedTab === 1) {
      fetchAutomationTestCases();
      return () => intervalId && clearInterval(intervalId);
    }
    
    // 🔹 Health Check Logic
    // Check if we need to fetch new data (every 5 minutes)
    const now = new Date();
    const needsFetch = !lastHealthFetchTime || 
                      (now - lastHealthFetchTime) / (1000 * 60) > 5;
    
    if (needsFetch || allHealthRecords.length === 0) {
      fetchAllHealthChecks();
      // Set up interval to refresh health checks every 5 minutes
      intervalId = setInterval(fetchAllHealthChecks, 5 * 60 * 1000);
    } else {
      // Filter existing records based on selected app and environment
      filterHealthRecords();
    }
  
    return () => intervalId && clearInterval(intervalId);
  }, [selectedApp, selectedEnv, selectedTab]);
  
  // Function to fetch all health checks at once using the new API endpoint
  const fetchAllHealthChecks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/healthcheck/latest`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}` // if needed
        }
      });   
      // Process the response data
      const healthData = response.data.map(record => ({
        id: record.id,
        app_name: record.app_name,
        environment: record.environment,
        url: record.url,
        status: record.status,
        checkedAt: record.checkedAt,
        error: record.error_message
      }));
      
      // Store all health records
      setAllHealthRecords(healthData);
      setLastHealthFetchTime(new Date());
      
      // Filter records based on current selection
      filterHealthRecords(healthData);
    } catch (err) {
      console.error('Error fetching health status data:', err.message);
      setHealthRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to filter health records based on selected app and environment
  const filterHealthRecords = (records = allHealthRecords) => {
    if (records.length === 0) return;
    
    // Use the same appNameMap for consistency
    const backendAppName = appNameMap[selectedApp];
    
    // Log for debugging
    console.log('Filtering health records for app:', selectedApp, 'mapped to:', backendAppName);
    
    
    // Filter records based on selected app and environment
    const filtered = records.filter(record => 
      record.app_name === backendAppName && 
      record.environment === selectedEnv
    );
    
    setHealthRecords(filtered);
    setFilteredRecords(filtered);
  };
  

  // Legacy function - kept for reference but no longer used with new approach
  const fetchBimigrator = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        "https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/healthcheck/bimigrator",
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Map response as needed, example:
      const record = {
        id: res.data.id,
        url: res.data.url,
        status: res.data.status,
        checkedAt: res.data.checkedAt,
        error: res.data.error ?? '-',
      };
      // Store it in component state accordingly
    } catch(e) {
      console.error("Failed to fetch BIMigrator health check", e);
    }
  }
  
  // Legacy function - kept for reference but no longer used with new approach
  const fetchCsdlcHealthCheck = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/health/check-health/csdlc-dev`);
      const apis = res.data.apis || [];
      const errorCount = apis.filter(api => ![200, 201, 400].includes(api.status)).length;
      const newRecord = {
        id: res.data.id,
        url: res.data.url,
        status: res.data.status,
        checkedAt: res.data.checkedAt,
        error: errorCount > 0 ? `${errorCount} API(s) failed` : '-',
      };
      setHealthRecords([newRecord]);
      setFilteredRecords([newRecord]);
    } catch (err) {
      console.error('Error fetching CSDLC health:', err.message);
      setHealthRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };


  // Legacy function - kept for reference but no longer used with new approach
  const fetchVaidhyameghaHealthCheck = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/health/check-health/vaidhyamegha?environment=${selectedEnv}`);
      const newRecord = {
        id: res.data.id,
        url: res.data.url,
        status: res.data.status,
        checkedAt: res.data.checkedAt,
        error: res.data.error,
        downloadUrl: res.data.logFilePath || null,
        environment: selectedEnv // Store the environment with the record
      };
      setHealthRecords([newRecord]);
      setFilteredRecords([newRecord]);
    } catch (err) {
      console.error('Error fetching Vaidhyamegha health:', err.message);
      setHealthRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Legacy function - kept for reference but no longer used with new approach
  const fetchSamyamaHealthCheck = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('https://fztfytoynxxetzgjxl.supabase.co/functions/v1/healthcheck/samyama', {
        params: { environment: selectedEnv }, // pass env param (e.g., DEV or PROD)
        headers: { Authorization: `Bearer ${token}` }
      });
      const record = {
        id: res.data.id,
        url: res.data.url,
        status: res.data.status,
        checkedAt: res.data.checkedAt,
        error: res.data.error || '-',
      };
      setHealthRecords([record]);
      setFilteredRecords([record]);
    } catch (err) {
      setHealthRecords([]);
      setFilteredRecords([]);
      console.error("Error fetching Samyama health:", err);
    } finally {
      setLoading(false);
    }
  };
  
  // Legacy function - kept for reference but no longer used with new approach
  const fetchServiceGatewayHealthCheck = async () => {
    try {
      setLoading(true);
      // Only fetch if DEV environment is selected
      if (selectedEnv === 'DEV') {
        const res = await axios.get(`${API_BASE}/api/health/check-health/service-gateway`);
        const newRecord = {
          id: res.data.id,
          url: 'https://dev.gateway.fs.vaidhyamegha.com',
          status: res.data.status,
          checkedAt: res.data.checkedAt,
          error: res.data.error
        };
        setHealthRecords([newRecord]);
        setFilteredRecords([newRecord]);
      }
    } catch (err) {
      console.error('Error fetching Service Gateway health:', err.message);
      setHealthRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomationTestCases = async () => {
    try {
      setLoading(true);
  
      // Map to backend-recognized app name
      const mappedAppName = appNameMap[selectedApp] || selectedApp;
  
      const res = await axios.get(
        'https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/test/latest',
        {
          params: {
            app_name: mappedAppName,
            env: selectedEnv.toLowerCase()
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
  
      if (res.data) {
        // Normalize the record for frontend display
        const normalizedRecord = {
          ...res.data,
          // Ensure the timestamp is properly formatted
          checkedAt: res.data.checked_at,
          // Ensure test counts are available as numbers
          pass_count: res.data.pass_count || 0,
          fail_count: res.data.fail_count || 0,
          total_count: res.data.total_count || 0
        };

        setAutomationRecords([normalizedRecord]);
        setFilteredRecords([normalizedRecord]);
      } else {
        setAutomationRecords([]);
        setFilteredRecords([]);
      }
    } catch (err) {
      console.error('Error fetching automation test cases:', err.message);
      setAutomationRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };
  
  

  const handleFilterOpen = (event) => setAnchorEl(event.currentTarget);
  const handleFilterClose = () => setAnchorEl(null);

  // Map frontend display names to backend app_name values using environment variables
  const appNameMap = {
    // Use the environment variables directly
    [process.env.REACT_APP_VAIDHYAMEGHA_APP_NAME]: process.env.REACT_APP_VAIDHYAMEGHA_APP_NAME,
    [process.env.REACT_APP_BIM_APP_NAME]: process.env.REACT_APP_BIM_APP_NAME,
    [process.env.REACT_APP_CSDLC_APP_NAME]: process.env.REACT_APP_CSDLC_APP_NAME,
    [process.env.REACT_APP_SAMYAMA_APP_NAME]: process.env.REACT_APP_SAMYAMA_APP_NAME,
    [process.env.REACT_APP_SERVICE_GATEWAY_APP_NAME]: process.env.REACT_APP_SERVICE_GATEWAY_APP_NAME
  };
  
  // Log the app name mapping for debugging
  console.log('App name mapping:', appNameMap, 'Selected app:', selectedApp, 'Mapped to:', appNameMap[selectedApp]);
  
  const applyDateFilter = async () => {
    if (!startDate || !endDate) return;
  
    try {
      setLoading(true);
      const from = formatDate(startDate);
      const to = formatDate(endDate);
  
      // Use the same appNameMap for consistency
      const mappedAppName = appNameMap[selectedApp] || selectedApp;
      const token = localStorage.getItem('token');
  
      // Log for debugging
      console.log('Applying date filter for app:', selectedApp, 'mapped to:', mappedAppName);
  
      if (selectedTab === 1) {
        // Test Cases filtering API (keep unchanged if no new endpoint)
        const res = await axios.get('https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/test/filter', {
          params: {
            app_name: mappedAppName,
            env: selectedEnv.toLowerCase(),
            from,
            to,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
          
        // Normalize test case data
        const mapped = res.data.map(item => ({
          ...item,
          checkedAt: item.checked_at,
          pass_count: item.pass_count || 0,
          fail_count: item.fail_count || 0,
          total_count: item.total_count || 0,
        }));
  
        setFilteredRecords(mapped);
  
      } else {
        // Health Checks filtering using new edge function endpoint
        const res = await axios.get(
          'https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/healthcheck/filter',
          {
            params: {
              app_name: mappedAppName,
              environment: selectedEnv,
              from,
              to,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
  
        // The edge function might return a single object or array, normalize to array
        const dataArray = Array.isArray(res.data) ? res.data : [res.data];
  
        // Map response to your frontend expected structure
        const healthData = dataArray.map(record => ({
          id: record.id,
          app_name: record.app_name,
          environment: record.environment,
          url: record.url,
          status: record.status,
          checkedAt: record.checkedAt,
          error: record.error_message || record.error || '',
        }));
  
        setFilteredRecords(healthData);
      }
    } catch (err) {
      console.error('Error applying date filter:', err.message || err);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
      handleFilterClose();
    }
  };
  
  const clearDateFilter = async () => {
    setStartDate(null);
    setEndDate(null);
  
    try {
      setLoading(true);
      const today = formatDate(new Date());
      const mappedAppName = appNameMap[selectedApp] || selectedApp;
      const token = localStorage.getItem('token');
  
      if (selectedTab === 1) {
        // Test Cases clear filter API (keep unchanged if no new endpoint)
        const res = await axios.get('https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/test/filter', {
          params: {
            app_name: mappedAppName,
            env: selectedEnv.toLowerCase(),
            from: '1970-01-01',
            to: today,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
  
        const mapped = res.data.map(item => ({
          ...item,
          checkedAt: item.checked_at,
          pass_count: item.pass_count || 0,
          fail_count: item.fail_count || 0,
          total_count: item.total_count || 0,
        }));
  
        const latest = mapped.length > 0 ? [mapped[0]] : [];
        setFilteredRecords(latest);
  
      } else {
        // Health Checks clear filter using new edge function endpoint
        const res = await axios.get(
          'https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/healthcheck/filter',
          {
            params: {
              app_name: mappedAppName,
              environment: selectedEnv,
              from: '1970-01-01',
              to: today,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
  
        const dataArray = Array.isArray(res.data) ? res.data : [res.data];
        const latest = dataArray.length > 0 ? [dataArray[0]] : [];
        setFilteredRecords(latest);
      }
    } catch (err) {
      console.error('Error clearing date filter:', err.message || err);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
      handleFilterClose();
    }
  };
    
  const open = Boolean(anchorEl);
  const id = open ? 'date-filter-popover' : undefined;

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#f9f9f9' }}>
      <style>
        {`
          .date-input {
            width: 100%;
            padding: 10px 14px;
            font-size: 14px;
            border: 1px solid #ccc;
            border-radius: 8px;
          }
        `}
      </style>
      <Box sx={{ width: '240px', borderRight: '1px solid #ddd', padding: '16px', backgroundColor: '#fff' }}>
        <Typography variant="h6" fontWeight="bold">Applications</Typography>
        {applications.map(app => (
          <Box
            key={app}
            sx={{ mt: 2, p: 1.5, borderRadius: 1, cursor: 'pointer', backgroundColor: selectedApp === app ? '#e8f0fe' : 'transparent', fontWeight: selectedApp === app ? 'bold' : 'normal', color: selectedApp === app ? '#1565c0' : '#333' }}
            onClick={() => setSelectedApp(app)}
          >
            {app}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, p: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>QA & Test Automation</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Run automated test suites, perform health checks, and track quality metrics.
        </Typography>

        <Tabs value={selectedTab} onChange={(e, newVal) => setSelectedTab(newVal)} sx={{ mb: 2 }}>
          {tabs.map((label, idx) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            {environments.map(env => (
              <Button
                key={env}
                variant={selectedEnv === env ? 'contained' : 'outlined'}
                onClick={() => setSelectedEnv(env)}
                sx={{ mr: 2 }}
              >
                {env}
              </Button>
            ))}
          </Box>
          <IconButton onClick={handleFilterOpen}><FilterListIcon /></IconButton>
        </Box>

        <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleFilterClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Box sx={{ p: 3, width: 300, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Filter by Date Range
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Start Date</Typography>
              <DatePicker selected={startDate} onChange={setStartDate} placeholderText="Select start date" dateFormat="yyyy-MM-dd" isClearable className="date-input" />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>End Date</Typography>
              <DatePicker selected={endDate} onChange={setEndDate} placeholderText="Select end date" dateFormat="yyyy-MM-dd" isClearable className="date-input" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={clearDateFilter} variant="outlined" color="error">Clear</Button>
              <Button onClick={applyDateFilter} variant="contained">Apply</Button>
            </Box>
          </Box>
        </Popover>

        <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>{selectedApp}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', pb: 1, fontWeight: 600, color: '#424242' }}>
            <Box>URL</Box>
            <Box>Status</Box>
            <Box>Checked At</Box>
            <Box>Download</Box>
          </Box>
          <Box sx={{ height: '1px', backgroundColor: '#e0e0e0', mb: 1 }} />
          {loading ? (
            <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={32} /></Box>
          ) : filteredRecords.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center', color: '#9e9e9e', fontStyle: 'italic' }}>No records found.</Box>
          ) : (
            filteredRecords.map((entry, idx) => (
              <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', alignItems: 'center', py: 1.5, borderTop: idx !== 0 ? '1px solid #f0f0f0' : 'none', fontSize: '0.95rem', color: '#424242' }}>
                <Box><a href={entry.url} target="_blank" rel="noreferrer" style={{ color: '#1565c0' }}>{entry.url}</a></Box>
                <Box sx={{color:entry.status === 'PASS' ? 'green' :entry.status === 'PARTIAL' ? 'orange' :entry.status === 'FAIL' ? 'red' :entry.status === 'healthy' ? 'green' :entry.status === 'unhealthy' ? 'red' :'inherit'}}>
                  {entry.status}
                  {selectedTab === 1 && entry.total_count && entry.total_count > 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '0.85rem' }}>
                      [{entry.status === 'FAIL' ? 
                        `${entry.fail_count || 0}/${entry.total_count}` : 
                        `${entry.pass_count || 0}/${entry.total_count}`}]
                    </span>
                  )}
                </Box>

                <Box sx={{ fontFamily: 'monospace' }}>{entry.checkedAt && !isNaN(new Date(entry.checkedAt)) ? 
                  (() => {
                    // Create a date object from the timestamp
                    const date = new Date(entry.checkedAt);
                    
                    // Format the date in IST (UTC+5:30)
                    return new Intl.DateTimeFormat('en-IN', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true,
                      timeZone: 'Asia/Kolkata' // Explicitly set to IST timezone
                    }).format(date);
                  })() : '–'}
                </Box>             
                <Box>
                <IconButton
  onClick={async () => {
    const token = localStorage.getItem('token');
    const dateStr = entry.checkedAt ? new Date(entry.checkedAt).toISOString().slice(0, 10) : 'unknown';

    try {
      let downloadUrl;
      let mimeType;
      let fileExt;

      if (selectedTab === 1) {
        // For automation tests - keep old URL or replace if you have new endpoint
        downloadUrl = `https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/test/download/${entry.id}`;
        mimeType = 'text/html';
        fileExt = 'html';
      } else {
        // For health checks use the new edge function endpoint
        downloadUrl = `https://fztfytnyyxxetzgjxl.supabase.co/functions/v1/healthcheck/download-csv-id/${entry.id}`;
        mimeType = 'text/csv;charset=utf-8';
        fileExt = 'csv';
      }

      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedTab === 1 ? 'automation-tests' : 'health-check'}_${dateStr}.${fileExt}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  }}
  title="Download Report"
>
  <DownloadIcon />
</IconButton>

</Box>
</Box>
            ))
          )}
        </Paper>
      </Box>
    </Box>
  );
}
export default QADashboard;